import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import { makeEditBankSchema } from '@/lib/payments/paymentAccount.schema';
import type { PaymentAccountFormValues } from '@/lib/payments/paymentAccount.types';

interface UseEditBankFormArgs {
  account: BankAccountAccount;
  /** Whether the billing sub-form must be filled (member opened "Change"). */
  billingRequired: boolean;
}

/**
 * Seeds the shared `PaymentAccountFormValues` shape from a saved bank account so the existing
 * bank/billing field components (fields.tsx, BillingInformationSection) work unchanged — the
 * `bank.routingNumber`/`accountNumber`/`bankName` slots are unused placeholders (all three are
 * immutable and not rendered on Edit). `bank.autoPay` seeds `false`; `AutoPayCheckbox` forces it to
 * the real enrollment status once `useMemberAutoPaySummary` resolves, same as the Add modal and
 * credit card's Edit. Billing seeds from the account's OWN saved address, not the member profile —
 * Edit shows per-payment-method billing, matching legacy and credit card's Edit.
 */
export function seedEditBankFormValues(account: BankAccountAccount): PaymentAccountFormValues {
  return {
    card: {
      cardNumber: '',
      expiry: '',
      cvv: '',
      isHsa: false,
      makeDefault: true,
      autoPay: true,
    },
    bank: {
      routingNumber: '',
      accountNumber: '',
      bankName: account.bankName,
      accountType: account.accountType,
      isHsa: account.isHsa,
      makeDefault: account.isDefault,
      autoPay: false,
    },
    billing: {
      name: account.holderName,
      line1: account.line1,
      line2: account.line2,
      city: account.city,
      state: account.state,
      zip: account.zip,
    },
  };
}

/** The RHF instance for the Edit Payment Account modal — bank account. */
export function useEditBankForm({ account, billingRequired }: UseEditBankFormArgs) {
  const argsRef = useRef({ billingRequired });
  argsRef.current = { billingRequired };

  const resolver = useMemo<Resolver<PaymentAccountFormValues>>(
    () => (values, context, options) => {
      const { billingRequired } = argsRef.current;
      const schema = makeEditBankSchema({ billingRequired });
      return (zodResolver(schema) as unknown as Resolver<PaymentAccountFormValues>)(values, context, options);
    },
    [],
  );

  // `useForm`'s `defaultValues` only take effect at initial mount — the orchestrator is expected
  // to key/remount the form per edit session (mirrors EditCreditCardModal's card variant), so
  // this memo doesn't need to guard against re-seeding over an in-progress edit.
  const defaultValues = useMemo(() => seedEditBankFormValues(account), [account]);

  return useForm<PaymentAccountFormValues>({ resolver, defaultValues, mode: 'onTouched' });
}
